import { useState, useEffect, useCallback } from 'react'
import { api } from '@/lib/api/axios-instance'

export const useTourManager = () => {
  const [shouldShowTour, setShouldShowTour] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const [refetchKey, setRefetchKey] = useState(0)

  // Clear old tour localStorage items on initialization
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('student-store')
      localStorage.removeItem('admin-store')
      localStorage.removeItem('teacher-tour-storage')
      localStorage.removeItem('super-admin-tour-storage')
      localStorage.removeItem('jerid-school-tour-storage')
    }
  }, [])

  const fetchTourStatus = useCallback(async () => {
    try {
      // Use HTTP-only cookies (automatic via withCredentials in axios instance)
      const response = await api.get('/user-controller/session')
      
      // Database is source of truth
      const userOnboarding = response.data?.userOnboarding
      setShouldShowTour(userOnboarding === false)
      setIsLoading(false)
    } catch (error: any) {
      // Handle 401 (not logged in) gracefully - this is expected before login
      if (error.response?.status === 401) {
        setShouldShowTour(false)
        setIsLoading(false)
      } else {
        // console.error('Tour Manager: Failed to fetch tour status from database:', error)
        // Safe default: don't show tours if DB unavailable
        setShouldShowTour(false)
        setIsLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    // Initial fetch (may fail with 401 if not logged in - that's OK)
    fetchTourStatus()
    
    // Listen for login events to refetch tour status after successful login
    const handleUserLogin = () => {
      // Small delay to ensure cookie is set
      setTimeout(() => {
        setIsLoading(true)
        fetchTourStatus()
      }, 100)
    }
    
    // Listen for tour status changes to refetch
    const handleTourStatusChange = () => {
      setTimeout(() => {
        setIsLoading(true)
        fetchTourStatus()
      }, 100)
    }
    
    window.addEventListener('user-login', handleUserLogin)
    window.addEventListener('tour-status-change', handleTourStatusChange)
    
    return () => {
      window.removeEventListener('user-login', handleUserLogin)
      window.removeEventListener('tour-status-change', handleTourStatusChange)
    }
  }, [fetchTourStatus])
  
  // Also refetch when refetchKey changes
  useEffect(() => {
    if (refetchKey > 0) {
      fetchTourStatus()
    }
  }, [refetchKey, fetchTourStatus])
  
  const updateTourStatus = async (showTour: boolean) => {
    try {
      // Use HTTP-only cookies (automatic via withCredentials in axios instance)
      await api.patch('/user-controller/update-onboarding', 
        { userOnboarding: showTour }
      )
      
      setShouldShowTour(showTour)
      
      // Trigger re-fetch to ensure all components get updated
      setRefetchKey(prev => prev + 1)
    } catch (error) {
      // console.error('Tour Manager: Failed to update tour status:', error)
    }
  }
  
  return { 
    shouldShowTour, 
    isLoading, 
    updateTourStatus 
  }
}
